import React from 'react';
import { DasTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { cn, isNonEmpty } from '@ais-ui/utils';
import { Separator } from '@ais-ui/ui-core';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { LastOriginDisplay } from './LastOriginDisplay';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

interface IdentificationProps {
  dasInfo: DasTargetInfo;
}

const Identification = ({ dasInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem label="Track ID" value={dasInfo.trackId} />
      <IdentificationItem label="Site" value={dasInfo.site} />
      <IdentificationItem label="Priority" value={dasInfo.priority} />
    </div>
  );
};

interface PositionProps {
  dasInfo: DasTargetInfo;
}

interface PositionItemProps {
  label: string;
  value: string;
  className?: string;
}

const PositionItem = ({ label, value, className }: PositionItemProps) => {
  return (
    <div className={cn('flex flex-col mt-1', className)}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

const Position = ({ dasInfo }: PositionProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <PositionItem
        label="Last Report Timestamp"
        value={formatDateTimeWithTimeZone(dasInfo.lastReportTimestamp)}
        className="col-span-2"
      />
      <PositionItem label="Lat 1" value={dasInfo.lat1} />
      <PositionItem label="Lon 1" value={dasInfo.lon1} />
      <PositionItem label="Lat 2" value={dasInfo.lat2} />
      <PositionItem label="Lon 2" value={dasInfo.lon2} />
    </div>
  );
};

interface TrackingDataProps {
  dasInfo: DasTargetInfo;
}

const TrackingDataItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

const TrackingData = ({ dasInfo }: TrackingDataProps) => {
  return (
    <div className="grid grid-cols-2 gap-1">
      <TrackingDataItem label="Tracked Accuracy" value={dasInfo.accuracy} />
      <TrackingDataItem label="Tracked Speed" value={dasInfo.speed} />
      <TrackingDataItem label="Tracked Heading 1" value={dasInfo.heading1} />
      <TrackingDataItem label="Tracked Heading 2" value={dasInfo.heading2} />
    </div>
  );
};

interface CableDataProps {
  dasInfo: DasTargetInfo;
}

const CableDataItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-xs whitespace-nowrap select-text">
        {isNonEmpty(value) ? value : 'Unknown'}
      </span>
    </div>
  );
};

const CableData = ({ dasInfo }: CableDataProps) => {
  return (
    <div className="grid grid-cols-2 gap-1">
      <CableDataItem
        label="Non-filtered Cable Position"
        value={dasInfo.nonFilteredCablePosition}
      />
      <CableDataItem
        label="Non-filtered Dist from Cable"
        value={dasInfo.nonFilteredDistFromCable}
      />
    </div>
  );
};

export const DasDetailsPanel = ({ dasInfo }: { dasInfo: DasTargetInfo }) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">DAS System Information</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification dasInfo={dasInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Position Data</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Position dasInfo={dasInfo}></Position>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Tracking Information</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <TrackingData dasInfo={dasInfo}></TrackingData>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Cable Data</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <CableData dasInfo={dasInfo}></CableData>
      </div>

      {dasInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={dasInfo.lastOrigin} />
      )}
    </div>
  );
};
